import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useToast } from '../../components/ds';
import { Button, Table, Badge, Spinner } from '../../components/ds';
import AddDelegationModal from './AddDelegationModal';
import AddCompanyModal from './AddCompanyModal';
import FeaturesModal, { TARGETING_OPTIONS } from './FeaturesModal';
import './AccountDetailPage.css';

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [account, setAccount] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [delegatedCompanies, setDelegatedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDelegation, setShowAddDelegation] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [features, setFeatures] = useState(null);
  const [disabling, setDisabling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [accounts, usersData, delegations, companiesData, featuresData] = await Promise.all([
        api.getAccounts(),
        api.getUsers(id),
        api.getDelegations(),
        api.getAccountCompanies(id),
        api.getAccountFeatures(id),
      ]);

      const acct = (accounts || []).find((a) => a.uuid === id);
      setAccount(acct || null);
      setUsers(usersData || []);
      setCompanies(companiesData || []);
      setFeatures(featuresData?.features || null);

      const myDelegations = (delegations || []).filter(
        (d) => d.delegator_ids && d.delegator_ids.includes(id)
      );

      const companiesWithApps = await Promise.all(
        myDelegations.map(async (d) => {
          try {
            const [companyDetail, apps] = await Promise.all([
              api.getCompanyDetail(d.delegatee_id),
              api.getApplications(d.delegatee_id),
            ]);
            return {
              delegationUUID: d.uuid,
              ...companyDetail,
              applications: apps || [],
            };
          } catch {
            return {
              delegationUUID: d.uuid,
              uuid: d.delegatee_id,
              company_name: 'Unknown Company',
              applications: [],
            };
          }
        })
      );

      setDelegatedCompanies(companiesWithApps);
    } catch (err) {
      addToast('Failed to load account details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable this account?')) return;
    setDisabling(true);
    try {
      await api.disableAccount(id);
      addToast('Account disabled', 'success');
      fetchData();
    } catch (err) {
      addToast('Failed to disable account: ' + err.message);
    } finally {
      setDisabling(false);
    }
  };

  const handleRemoveDelegation = async (delegationUUID, companyName) => {
    if (!window.confirm(`Remove delegation for "${companyName}"?`)) return;
    try {
      await api.deleteDelegation(delegationUUID);
      addToast('Delegation removed', 'success');
      fetchData();
    } catch (err) {
      addToast('Failed to remove delegation: ' + err.message);
    }
  };

  const handleDelegationAdded = () => {
    setShowAddDelegation(false);
    addToast('Delegation added', 'success');
    fetchData();
  };

  const handleRemoveCompany = async (companyId, companyName) => {
    if (!window.confirm(`Remove company "${companyName}"?`)) return;
    try {
      await api.removeAccountCompany(id, companyId);
      addToast('Company removed', 'success');
      fetchData();
    } catch (err) {
      addToast('Failed to remove company: ' + err.message);
    }
  };

  const handleCompanyAdded = () => {
    setShowAddCompany(false);
    addToast('Company added', 'success');
    fetchData();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatTimezone = (tz) => {
    if (!tz) return '-';
    try {
      const parsed = JSON.parse(tz);
      return parsed.name || tz;
    } catch {
      return tz;
    }
  };

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  if (!account) {
    return <div className="loading">Account not found</div>;
  }

  return (
    <div>
      {/* Back Button */}
      <Button variant="secondary" onClick={() => navigate('/accounts')} style={{ marginBottom: 16 }}>
        ← Back to Accounts
      </Button>

      {/* Account Info */}
      <div className="detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div className="detail-info">
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>{account.account_name}</h1>
          <div className="detail-meta" style={{ display: 'flex', gap: 16, marginTop: 8, color: 'var(--text-secondary)', fontSize: 14 }}>
            <span>Timezone: {formatTimezone(account.timezone)}</span>
            <span>
              Status: <Badge variant={account.account_status === 'active' ? 'primary' : 'default'}>{account.account_status}</Badge>
            </span>
          </div>
        </div>
        {account.account_status === 'active' && (
          <Button
            variant="secondary"
            style={{ color: 'var(--status-error-text)' }}
            onClick={handleDisable}
            disabled={disabling}
          >
            {disabling ? 'Disabling...' : 'Disable Account'}
          </Button>
        )}
      </div>

      {/* Users */}
      <div className="detail-section" style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 16 }}>Users</h2>
        <Table
          columns={[
            { key: 'user_name', title: 'User Name' },
            { key: 'email', title: 'Email' },
            { key: 'role', title: 'Role', render: (_, row) => (row.account_user_attribute?.role_ids || row.role_ids || []).join(', ') },
            { key: 'CreatedAt', title: 'Created', render: (val) => formatDate(val) },
          ]}
          data={users}
          rowKey="uuid"
          emptyText="No users"
        />
      </div>

      {/* Feature Settings */}
      <div className="detail-section" style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2>Feature Settings</h2>
          <Button variant="secondary" onClick={() => setShowFeaturesModal(true)}>Edit</Button>
        </div>
        <div style={{ padding: 20, background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
          <h3 style={{ marginBottom: 12, fontSize: 15, color: 'var(--text-secondary)' }}>
            Targeting
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {TARGETING_OPTIONS.map((opt) => (
              <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  checked={features?.targeting?.[opt.key] || false}
                  disabled
                  style={{ width: 18, height: 18 }}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Companies (SaaS DB) */}
      <div className="detail-section" style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2>Companies</h2>
          <Button onClick={() => setShowAddCompany(true)}>+ Add Company</Button>
        </div>
        <Table
          columns={[
            { key: 'company_id', title: 'Company ID', render: (val) => <code>{val}</code> },
            { key: 'company_name', title: 'Company Name' },
            { key: 'created_at', title: 'Created At', render: (val) => formatDate(val) },
            { key: 'actions', title: 'Actions', render: (_, row) => (
              <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); handleRemoveCompany(row.company_id, row.company_name); }}>
                Remove
              </Button>
            )},
          ]}
          data={companies}
          rowKey="company_id"
          emptyText="No companies associated"
        />
      </div>

      {/* Delegated Companies */}
      <div className="detail-section" style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2>Delegated Companies</h2>
          <Button onClick={() => setShowAddDelegation(true)}>+ Add Delegation</Button>
        </div>

        {delegatedCompanies.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32, background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            No delegated companies
          </div>
        ) : (
          delegatedCompanies.map((company) => (
            <div key={company.delegationUUID} className="company-card" style={{ padding: 20, marginBottom: 12, background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
              <div className="company-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3>{company.company_name}</h3>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRemoveDelegation(company.delegationUUID, company.company_name)}
                >
                  Remove
                </Button>
              </div>
              <div className="company-details" style={{ display: 'flex', gap: 16, fontSize: 14, color: 'var(--text-secondary)' }}>
                {company.tax_id_number && <span>Tax ID: {company.tax_id_number}</span>}
                {company.address && <span>Address: {company.address}</span>}
                {(company.phone_area_code || company.phone_number) && (
                  <span>Phone: {company.phone_area_code}{company.phone_area_code && '-'}{company.phone_number}</span>
                )}
              </div>
              {company.applications && company.applications.length > 0 && (
                <div className="company-apps" style={{ marginTop: 12 }}>
                  <h4 style={{ fontSize: 14, marginBottom: 6 }}>Applications</h4>
                  <ul style={{ paddingLeft: 20, fontSize: 14 }}>
                    {company.applications.map((app) => (
                      <li key={app.uuid}>
                        {app.application_name}
                        <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>({app.platform})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showAddCompany && (
        <AddCompanyModal
          accountId={id}
          onClose={() => setShowAddCompany(false)}
          onAdded={handleCompanyAdded}
        />
      )}

      {showAddDelegation && (
        <AddDelegationModal
          accountId={id}
          onClose={() => setShowAddDelegation(false)}
          onAdded={handleDelegationAdded}
        />
      )}

      {showFeaturesModal && (
        <FeaturesModal
          accountUuid={id}
          features={features}
          onClose={() => setShowFeaturesModal(false)}
          onSaved={(updatedFeatures) => {
            setFeatures(updatedFeatures);
            setShowFeaturesModal(false);
            addToast('Feature settings updated', 'success');
          }}
        />
      )}
    </div>
  );
}
